type EventCallback = (data?: any) => void;

export default class EventEmitter {
    private events: { [key: string]: EventCallback[] } = {};
    private shouldDebugListenerLifecycle(event: string) {
        const ctorName =
            typeof (this as any)?.constructor?.name === "string"
                ? (this as any).constructor.name
                : "UnknownEmitter";
        const trackedEmitters = new Set([
            "SofyaTranscriber",
            "WhisperTranscriptionAdapter",
            "OracleTranscriptionAdapter",
        ]);
        const trackedEvents = new Set([
            "ready",
            "recognizing",
            "recognized",
            "recognized_diarization",
            "connected",
            "disconnected",
            "reconnecting",
            "reconnected",
            "resilience_status",
            "telemetry",
            "telemetry_row",
            "batch_reprocess_status",
            "stt_session",
            "stopped",
            "error",
        ]);
        return trackedEmitters.has(ctorName) && trackedEvents.has(event);
    }

    private logListenerLifecycle(action: "on" | "off", event: string) {
        if (!this.shouldDebugListenerLifecycle(event)) {
            return;
        }

        const ctorName =
            typeof (this as any)?.constructor?.name === "string"
                ? (this as any).constructor.name
                : "UnknownEmitter";
        const count = this.events[event]?.length ?? 0;
        console.count(`listeners:${ctorName}:${event}:${action}`);
        console.log(`[ListenerDebug] ${ctorName}.${event} ${action}`, {
            listenerCount: count,
        });
    }

    /**
     * Registers a listener for an event.
     *
     * Important lifecycle contract:
     * - Callers that register with `on()` are responsible for calling `off()`
     *   when the listener is no longer needed, unless the entire emitter instance
     *   is guaranteed to be discarded immediately after use.
     * - Registering the same callback multiple times will produce multiple invocations.
     */
    on(event: string, callback: EventCallback) {
        if (!this.events[event]) {
            this.events[event] = [];
        }
        this.events[event].push(callback);
        this.logListenerLifecycle("on", event);
        return this;
    }

    /**
     * Registers a one-shot listener that removes itself after first invocation.
     *
     * Prefer `once()` when the event is expected exactly once (for example, "ready")
     * to reduce manual listener management burden.
     */
    once(event: string, callback: EventCallback) {
        const wrappedCallback: EventCallback = (data?: any) => {
            this.off(event, wrappedCallback);
            callback(data);
        };

        return this.on(event, wrappedCallback);
    }

    /**
     * Unregisters a previously registered listener for an event.
     *
     * This method is idempotent: if the event/callback pair is not present,
     * no exception is thrown and the emitter is unchanged.
     */
    off(event: string, callback: EventCallback) {
        const listeners = this.events[event];
        if (!listeners) {
            return this;
        }

        this.events[event] = listeners.filter(listener => listener !== callback);

        if (this.events[event].length === 0) {
            delete this.events[event];
        }

        this.logListenerLifecycle("off", event);

        return this;
    }

    /**
     * Returns the number of listeners currently registered for an event.
     */
    listenerCount(event: string) {
        return this.events[event]?.length ?? 0;
    }

    /**
     * Removes all listeners for one event, or all events when omitted.
     *
     * Use with care in shared emitters: it can detach listeners owned by other
     * consumers when called without a specific `event`.
     */
    removeAllListeners(event?: string) {
        if (event) {
            delete this.events[event];
            return this;
        }

        this.events = {};
        return this;
    }

    /**
     * Dispatches an event to the current listener snapshot.
     *
     * The listener array is cloned before iteration to avoid mutation issues
     * when listeners call `off()` or `on()` during event handling.
     */
    protected emit(event: string, data?: any) {
        const eventListeners = this.events[event];
        if (eventListeners) {
            eventListeners.slice().forEach(callback => callback(data));
        }
    }
}
